goog.declareModuleId('yext.ui.components.Tabs');

import {Clickable} from '/ui/components/clickable/clickable';
import {MenuController} from '/ui/components/menu/menucontroller';
import {classNames} from '/ui/lib/classnames';

import * as styles from '/ui/components/tabs/tabs.module.scss';
/**
 * A component that renders a list of tabs and renders content based upon the currently selected
 * tab. Tabs are hidden with CSS when out of view. Active tab state is pushed to the url for
 * linking and tab changes are pushed to window.history.
 *
 * To use this component, Tab children components must be passed to this component. Each child tab
 * component must contain its own child which represents the content to render when the tab is
 * active.
 *
 * As an example, this is how one would render two tabs "Foo" and "Bar" with content of "this is
 * foo" and "this is bar":
 * <Tabs>
 *   <Tab label={"Foo"}>
 *     {"this is foo"}
 *   </Tab>
 *   <Tab label={"Bar"}>
 *     {"this is bar"}
 *   </Tab>
 * </Tabs>
 *  @param {{
 *   className?: string,
 *   selectedTab?: string,
 *   tid?: string,
 *   pendoId?: string,
 *   onTabChange: !(function(string):void),
 *   tabLabelStyle?: string,
 *   noContentMargin?: boolean,
 *   children?: React.ReactNode,
 *  }} props
 * @return {React.ReactElement}
 */
export function Tabs({
  className,
  selectedTab,
  tid,
  pendoId,
  onTabChange,
  tabLabelStyle,
  noContentMargin,
  children,
}) {
  /**
   * Children can either be an array or a single object. This allows us to deal with them as if they
   * are always an array.
   * Also filters out all 'null' children.
   */
  // type casting children results in childrenArray might be null type error in later code
  // @ts-ignore
  const childrenArray = [].concat(children).filter(child => child);


  if (childrenArray.length === 0) {
    throw new Error('Tabs must have at least 1 child component');
  }

  const [tabsBreakPoint, setTabsBreakPoint] = React.useState(
    childrenArray.length,
  );

  const tabsRef = React.useRef(/** @type {HTMLUListElement|null} */(null));
  const recalculateBreakPoint = React.useCallback(() => setTabsBreakPoint(getTabsBreakPoint(tabsRef)), []);
  const getActiveTab = () =>
    childrenArray
      .map(child => child.props.id)
      .find(id => id === selectedTab)
    || childrenArray.filter(child => !child.props.disabled)[0].props.id;

  const [activeTab, setActiveTab] = React.useState(getActiveTab);

  React.useEffect(() => {
    const observer = new ResizeObserver(() => {
      window.requestAnimationFrame(recalculateBreakPoint);
    });
    const tabsContainer = tabsRef.current;
    if (tabsContainer) {
      observer.observe(tabsContainer);
    }
    return () => {
      if (tabsContainer) {
        observer.unobserve(tabsContainer);
      }
    };
  }, [recalculateBreakPoint]);

  React.useEffect(recalculateBreakPoint, [recalculateBreakPoint, children, activeTab]);

  const disabledTabIds = new Set(
    childrenArray
      .filter(child => child.props.disabled)
      .map(child => child.props.id),
  );

  if (selectedTab && disabledTabIds.has(selectedTab)) {
    throw new Error('Selected tab is disabled');
  }

  React.useEffect(() => {
    setActiveTab(getActiveTab);
  }, [selectedTab]); // eslint-disable-line


  /**
   * @param {string} id
   */
  const handleTabChange = id => {
    if (
      childrenArray.some(child => child.props.id === id)
      && !disabledTabIds.has(id)
    ) {
      setActiveTab(id);
      onTabChange(id);
    }
  };

  const tabContents = childrenArray.map(child => {
    const {children, id} = child.props;
    return (
      <div
        className={'yext__tab__content' + (id === activeTab ? '' : '--hidden')}
        key={id}
        id={id}
        role="tabpanel"
        aria-labelledby={'yext__tab-' + id}
      >
        {children}
      </div>
    );
  });

  const tabContainerClassNames = ['yext__tabs__container'];
  if (className) {
    tabContainerClassNames.push(className);
  }

  const tabLabelClassNames = ['yext__tab__selector__item__label'];
  if (tabLabelStyle) {
    tabLabelClassNames.push(tabLabelStyle);
  }

  const hasImages =
    childrenArray.filter(child => {
      const {imagePath} = child.props;
      return !!imagePath;
    }).length > 0;

  reorderChildrenIfNecessary(childrenArray, tabsBreakPoint, activeTab);


  const hiddenTabs = childrenArray.slice(tabsBreakPoint);
  const hiddenTabsActions = hiddenTabs.map(child => {
    const {label, id} = child.props;
    return {
      name: label,
      callback: () => handleTabChange(id),
    };
  });

  const selectorStyles = classNames(
    'yext__tab__selector__container',
    {
      'yext__tab__selector__container--with-images': hasImages,
      [styles.noMargin]: noContentMargin,
    },
  );
  return (
    <div className={tabContainerClassNames.join(' ')} data-pendo={pendoId}>
      <div className={selectorStyles}>
        <ul className="yext__tab__selector__list" role="tablist" ref={tabsRef}>
          {childrenArray
            .map((child, index)=> {
              const {imagePath, label, id, tid, pendoId, disabled} = child.props;
              const isActive = id === activeTab;
              const classNames = ['yext__tab__selector__item'];
              if (isActive) {
                classNames.push('yext__tab--active');
              }
              if (disabled) {
                classNames.push('yext__tab--disabled');
              }
              if (index >= tabsBreakPoint) {
                classNames.push(styles.tabsHidden);
                classNames.push('js-tabs-hidden');
              }

              return (
                <Clickable
                  tag="li"
                  key={id}
                  className={classNames.join(' ')}
                  onClick={() => {
                    handleTabChange(id);
                  }}
                  tid={tid}
                  data-pendo={pendoId}
                  id={'yext__tab-' + id}
                  role="tab"
                  aria-disabled={disabled}
                  aria-controls={id}
                  aria-selected={isActive}
                >
                  {imagePath && (
                    <img
                      src={imagePath}
                      alt=""
                      className="yext__tab__selector__item__image"
                    />
                  )}
                  <div className={tabLabelClassNames.join(' ')}>
                    {label}
                  </div>
                </Clickable>
              );
            })}
        </ul>
        {(tabsBreakPoint < childrenArray.length) && (
          <div className="yext__tab__selector__container__more">
            <MenuController
              actions={hiddenTabsActions}
              isContextual={true}
              alignMenu="right"
            />
          </div>
        )}
      </div>
      {tabContents}
    </div>
  );
}

/**
 * This function works by finding the first tab that results in a width larger than the
 * total width of the tabs container. Once found, that index is returned as the
 * breakpoint for deciding what stays in the displayed tabs and what goes into the
 * overflow.
 * @param {!React.RefObject<HTMLUListElement>} tabsRef
 * @return {number}
 */
const getTabsBreakPoint = tabsRef => {
  if (tabsRef.current == null) {
    return 0;
  }
  const tabsBCR = tabsRef.current.getBoundingClientRect();
  const totalWidth = tabsBCR.width;
  const startingTabOffset = tabsBCR.left;
  const allTabs = [...tabsRef.current.children];
  const findFirstOverflowIndex = element => {
    const elementBCR = element.getBoundingClientRect();
    return elementBCR.left + elementBCR.width - (totalWidth + startingTabOffset) > 0.01;
  };
  const index = allTabs.findIndex(findFirstOverflowIndex);
  if (index < 0) {
    return allTabs.length;
  }
  return index;
};

const reorderChildrenIfNecessary = (childrenArray, tabsBreakPoint, activeTabId) => {
  const childrenArrayIds = childrenArray.map(child => child.props.id);
  const activeIndex = childrenArrayIds.indexOf(activeTabId);
  if (activeIndex >= tabsBreakPoint) {
    const activeTab = childrenArray.splice(activeIndex, 1)[0];
    childrenArray.splice(0, 0, activeTab);
  }
  return true;
};
